/* eslint-disable react/prop-types */
/**
 * AlignmentSuggestBanner
 * ======================
 * iter184 — non-blocking soft-suggest above the pricing grid. Only
 * renders when triggers fire AND no alignment recommendation exists
 * yet. Single banner, even when multiple repair lines would trigger
 * it (the engine consolidates) — per user spec: "must not become
 * noisy".
 *
 * Wording is fixed (deterministic spec):
 *   "Wheel alignment is commonly recommended following these findings."
 *
 * CTA: "Add Wheel Alignment" — Feb 2026 fix: now POSTs directly to
 * the alignment recommendation endpoint and calls onAdded() so the
 * parent refreshes. Previously it relied on a brittle
 * `document.querySelector('[data-testid="add-recommendation-btn"]')`
 * which silently no-op'd when the target button wasn't on the page,
 * leaving the customer with no way to add the recommendation from
 * the banner.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, MoveRight, X } from "lucide-react";

export default function AlignmentSuggestBanner({
  yestimateId,
  refreshKey = 0,
  onAdded,
}) {
  const [suggestion, setSuggestion] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!yestimateId) return;
    setDismissed(false);
    axios.get(
      `${API}/foh/yestimate/${yestimateId}/recommendations/alignment/suggestions`,
    )
      .then((r) => setSuggestion(r.data))
      .catch(() => setSuggestion(null));
  }, [yestimateId, refreshKey]);

  if (!suggestion || dismissed) return null;
  if (suggestion.already_present) return null;
  if (!(suggestion.active_triggers || []).length) return null;

  const badges = Array.from(
    new Set((suggestion.trigger_details || []).map((t) => t.badge)),
  );

  const handleAdd = async () => {
    if (saving) return;
    try {
      setSaving(true);
      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/recommendations/alignment`,
        {
          alignment_type: suggestion.recommended_alignment_type || "four_wheel",
          trigger_reasons: suggestion.active_triggers || [],
          custom_note: "Auto-added from soft-suggest banner.",
        },
      );
      toast.success("Wheel alignment added as a separate pricing item.");
      setDismissed(true); // hide banner immediately for cleaner UX
      onAdded?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to add alignment recommendation.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="rounded-lg border-2 border-amber-300 bg-amber-50/70 px-4 py-3 mb-4 flex items-start gap-3"
      data-testid="alignment-suggest-banner"
    >
      <MoveRight className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-amber-900 leading-snug">
          Wheel alignment is commonly recommended following these findings.
        </p>
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {badges.map((b) => (
              <span
                key={b}
                className="text-[10.5px] uppercase bg-white text-amber-800 border border-amber-300 rounded px-1.5 py-0.5"
              >
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={saving}
          className="bg-amber-700 hover:bg-amber-800 text-white text-[12.5px] h-8 disabled:opacity-60"
          data-testid="alignment-suggest-add-btn"
        >
          {saving ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Adding…</>
          ) : (
            "Add Wheel Alignment"
          )}
        </Button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-amber-700 hover:text-amber-900 p-1 rounded hover:bg-amber-100"
          title="Dismiss (session only)"
          data-testid="alignment-suggest-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </section>
  );
}
